/**
 * AddIntegrationDialog
 *
 * Dialog for adding a new analytics integration.
 * Shows platform selection and configuration form.
 *
 * @module analytics-settings/components
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Stepper,
  Step,
  StepLabel,
  TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SDialog } from '@modules/ui-components';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import type {
  AnalyticsPlatform,
  PlatformConfig,
  AnalyticsIntegration,
} from '../types/analytics.types';
import { PLATFORM_INFO, getDefaultConfig } from '../types/analytics.types';
import { PlatformEditor } from './platform-editors';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

export interface AddIntegrationDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (platform: AnalyticsPlatform, name: string, config: PlatformConfig) => Promise<void>;
  existingIntegrations: AnalyticsIntegration[];
  /** Project ID - required for server-side platforms (Facebook CAPI test events) */
  projectId?: string;
  /**
   * Class-D launcher preset (ACTION_ROUTING A3/A7-A9): opens on the Configure step with
   * this platform selected. Ignored when the platform is already added (the picker shows "Added").
   */
  initialPlatform?: AnalyticsPlatform;
}

const STEPS = ['Select Platform', 'Configure'];

/**
 * AddIntegrationDialog - Add new integration wizard
 */
export const AddIntegrationDialog: React.FC<AddIntegrationDialogProps> = ({
  open,
  onClose,
  onAdd,
  existingIntegrations,
  projectId,
  initialPlatform,
}) => {
  const guideService = React.useMemo(() => new GuideService(), []);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPlatform, setSelectedPlatform] = useState<AnalyticsPlatform | null>(null);
  const [name, setName] = useState('');
  const [config, setConfig] = useState<PlatformConfig | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset state when dialog opens/closes
  const handleClose = useCallback(() => {
    setActiveStep(0);
    setSelectedPlatform(null);
    setName('');
    setConfig(null);
    onClose();
  }, [onClose]);

  // Handle platform selection
  const handlePlatformSelect = useCallback((platform: AnalyticsPlatform) => {
    setSelectedPlatform(platform);
    setName(PLATFORM_INFO[platform].name);
    setConfig(getDefaultConfig(platform));
    setActiveStep(1);
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!selectedPlatform || !config || !name.trim()) return;

    setSaving(true);
    try {
      await onAdd(selectedPlatform, name.trim(), config);
      handleClose();
    } finally {
      setSaving(false);
    }
  }, [selectedPlatform, config, name, onAdd, handleClose]);

  // Check if platform already exists (for single-instance platforms)
  const isPlatformUsed = useCallback(
    (platform: AnalyticsPlatform): boolean => {
      // Custom script can be added multiple times
      if (platform === 'custom_script') return false;
      return existingIntegrations.some((i) => i.platform === platform);
    },
    [existingIntegrations]
  );

  // Apply the preset once per open; re-arm when the dialog closes.
  const initialPlatformAppliedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initialPlatformAppliedRef.current = false;
      return;
    }
    if (!initialPlatform || initialPlatformAppliedRef.current) return;
    initialPlatformAppliedRef.current = true;
    if (isPlatformUsed(initialPlatform)) return; // keep the picker; the card carries the "Added" badge
    handlePlatformSelect(initialPlatform);
  }, [open, initialPlatform, isPlatformUsed, handlePlatformSelect]);

  // Platforms list (ordered)
  // Client-side platforms first, then server-side
  const platforms: AnalyticsPlatform[] = [
    // Client-side
    'segmently_internal',
    'amplitude',
    'ga4',
    'gtm',
    'mixpanel',
    'posthog',
    'facebook_pixel',
    'tiktok_pixel',
    'custom_script',
    // Server-side (for Stripe webhook events)
    'facebook_capi',
    'ga4_measurement_protocol',
    'amplitude_server',
    'mixpanel_server',
    'posthog_server',
    'tiktok_events_api',
  ];

  return (
    <SDialog
      open={open}
      onClose={handleClose}
      title="Add Analytics Integration"
      maxWidth="md"
      fullWidth
      data-testid={ANALYTICS_SETTINGS_TEST_IDS.addDialog}
    >
      <Box sx={{ p: 3 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Platform Selection */}
        {activeStep === 0 && (
          <Grid container spacing={2}>
            {platforms.map((platform) => {
              const info = PLATFORM_INFO[platform];
              const isUsed = isPlatformUsed(platform);

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={platform}>
                  <Card
                    variant="outlined"
                    data-testid={ANALYTICS_SETTINGS_TEST_IDS.platformCard(platform)}
                    sx={{
                      height: '100%',
                      opacity: isUsed ? 0.5 : 1,
                      position: 'relative',
                    }}
                  >
                    <CardActionArea
                      onClick={() => handlePlatformSelect(platform)}
                      disabled={isUsed}
                      data-testid={`analytics-platform-option-${platform}`}
                      sx={{ height: '100%' }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Typography variant="h4">{info.icon}</Typography>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {info.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {info.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                    {isUsed && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography variant="caption" color="success.main">
                          Added
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Step 2: Configuration */}
        {activeStep === 1 && selectedPlatform && config && (
          <Box>
            {/* Integration Name */}
            <WithGuideHelp
              guideKey={GUIDE_ANCHORS.analyticsProviderConfig.guideKey}
              guideService={guideService}
              data-testid={GUIDE_ANCHORS.analyticsProviderConfig.testId}
              position="top-right"
              sx={{ display: 'block' }}
            >
              <TextField
                label="Integration Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                sx={{ mb: 3 }}
                helperText="A friendly name to identify this integration"
                slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.addNameInput } }}
              />
            </WithGuideHelp>

            {/* Platform-specific Configuration */}
            <PlatformEditor
              platform={selectedPlatform}
              config={config}
              name={name}
              onChange={(newConfig) => setConfig(newConfig)}
              disabled={saving}
              projectId={projectId}
              siblingIntegrations={existingIntegrations}
            />

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                onClick={() => setActiveStep(0)}
                disabled={saving}
                data-testid={ANALYTICS_SETTINGS_TEST_IDS.addBackBtn}
              >
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  onClick={handleClose}
                  disabled={saving}
                  data-testid={ANALYTICS_SETTINGS_TEST_IDS.addCancelBtn}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !name.trim()}
                  data-testid="analytics-add-integration-save"
                >
                  {saving ? 'Adding...' : 'Add Integration'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </SDialog>
  );
};

export default AddIntegrationDialog;
